import React, { useEffect } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import { DateTime } from 'luxon';
import Chip from '@/components/General/Chip';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import MilestoneEditor from '@/components/Milestones/MilestoneEditor';
import { Query } from '@/utils/react-query/Queries';
import { MILESTONES_ENABLED } from '@/config/milestones';
import { canDeleteMilestones, canEditMilestones, canViewMilestoneManagement } from '@/utils/milestonePermissions';
import useMilestoneMutations from '@/utils/hooks/useMilestoneMutations';
import { showAlert } from '@/utils/showAlert';

/**
 * Milestone detail/edit. The save is atomic with the loaded
 * configurationRevision; a MILESTONE_REVISION_CONFLICT reloads the
 * definition so the editor shows the latest tiers.
 */
export default function MilestoneDetailScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const { milestoneID } = useLocalSearchParams();
    const { data: loggedInUser, isLoading: userLoading } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;
    const allowed = canViewMilestoneManagement(loggedInUser);

    const { data: catalog, isLoading: catalogLoading } = Query.milestones.catalog();
    const definitionQuery = Query.milestones.definition({ organizationID, milestoneID, options: { enabled: allowed } });
    const definition = definitionQuery.data;

    const { save, archive, duplicate } = useMilestoneMutations({ organizationID });

    const archived = !!definition?.deletedAt;
    const canManage = canEditMilestones(loggedInUser) || canDeleteMilestones(loggedInUser);

    const handleArchive = () => {
        if (!definition) return;
        showAlert({
            title: `Archive "${definition.name}"?`,
            subtitle: 'The series stops earning new tiers, but every earned badge stays visible in profiles and history. This cannot be undone.',
            isDestructive: true,
            confirmText: 'Archive',
            onConfirm: () => archive.mutate({ milestoneID: definition.id, expectedRevision: definition.configurationRevision }, {
                onSuccess: () => router.replace('/settings/organization/milestones'),
            }),
        });
    };

    const handleDuplicate = () => {
        if (!definition) return;
        showAlert({
            title: `Duplicate "${definition.name}"?`,
            subtitle: 'This creates a NEW disabled series with its own badge identity and thresholds — it does not carry over earned badges, and it will not count until you enable it.',
            confirmText: 'Duplicate',
            onConfirm: () => duplicate.mutate(definition),
        });
    };

    // Overflow actions live in the navigation header menu — the body keeps
    // only the status chips. Runs above the early returns so hook order stays
    // stable while the definition loads or the permission check fails.
    useEffect(() => {
        if (!allowed || !definition || !canManage) {
            navigation.setOptions({ headerRight: () => null });
            return;
        }
        const options = [
            {
                name: 'Duplicate',
                icon: { android: 'copy', ios: 'doc.on.doc', web: 'copy-outline' },
                disabled: duplicate.isPending,
                onSelect: handleDuplicate,
            },
            canDeleteMilestones(loggedInUser) && !archived && {
                name: 'Archive',
                icon: dropdownIcons.archive,
                destructive: true,
                disabled: archive.isPending,
                onSelect: handleArchive,
            },
        ].filter(Boolean);
        navigation.setOptions({
            headerRight: () => options.length > 0 ? <DropdownMenu testID="milestone-detail-menu" options={options} /> : null,
        });
    }, [navigation, allowed, definition, canManage, archived, archive.isPending, duplicate.isPending, loggedInUser]);

    if (userLoading || (allowed && (catalogLoading || definitionQuery.isLoading))) {
        return <View style={styles.center}><ActivityIndicator /></View>;
    }

    if (!MILESTONES_ENABLED || !allowed) {
        return (
            <View style={styles.center} testID="milestone-detail-requires-permission">
                <EmptyListPlaceholder noLottie spacing={48} message="You don't have permission to manage milestones." />
            </View>
        );
    }

    if (definitionQuery.isError || !definition) {
        return (
            <View style={styles.center} testID="milestone-not-found">
                <EmptyListPlaceholder noLottie spacing={48} message="That milestone could not be found. It may have been removed." />
            </View>
        );
    }

    const editable = canEditMilestones(loggedInUser);

    const handleSubmit = (payload) => {
        save.mutate({ definition: payload, expectedRevision: definition.configurationRevision }, {
            onError: (error) => {
                if (error?.milestoneCode === 'MILESTONE_REVISION_CONFLICT') {
                    // Reload so the editor shows the latest tiers/revision.
                    definitionQuery.refetch();
                }
            },
        });
    };

    return (
        <View style={styles.container} testID="milestone-detail-screen">
            <View style={styles.headerBar}>
                <View style={styles.headerChips}>
                    {definition.builtinKey && <Chip colorVariant="admin" icon="star-outline">Built-in</Chip>}
                    {archived && <Chip colorVariant="warning" icon="archive-outline">Archived {DateTime.fromISO(definition.deletedAt).toLocaleString(DateTime.DATE_MED)}</Chip>}
                </View>
            </View>

            {!editable && (
                <View style={styles.readOnlyNote}>
                    <Unicon name="eye-outline" size={16} colorVariant="textSecondary" />
                    <Text style={styles.readOnlyText}>You can view this milestone but only users with Edit Milestones can change it.</Text>
                </View>
            )}

            <MilestoneEditor
                key={`${definition.id}-${definition.configurationRevision}`}
                catalog={catalog}
                definition={definition}
                loggedInUser={loggedInUser}
                saving={save.isPending}
                readOnly={!editable}
                onSubmit={editable ? handleSubmit : () => Toast.show({ type: 'info', text2: 'You need Edit Milestones to save changes.' })}
                onCancel={() => router.back()}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
    },
    flex: {
        flex: 1,
    },
    headerBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.sm,
        paddingHorizontal: spacing.md,
        paddingTop: spacing.md,
        flexWrap: 'wrap',
        _web: {
            maxWidth: 720,
            alignSelf: 'center',
            width: '100%',
        },
    },
    headerChips: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        flexWrap: 'wrap',
    },
    readOnlyNote: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        marginHorizontal: spacing.md,
        marginTop: spacing.sm,
        padding: spacing.sm,
        backgroundColor: colors.surfaceVariant,
        borderRadius: roundness.md,
        _web: {
            maxWidth: 688,
            alignSelf: 'center',
            width: '100%',
        },
    },
    readOnlyText: {
        flex: 1,
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
}));
